import React from 'react';
import { useNavigate } from 'react-router-dom';
import { DownloadIcon, ClockIcon, TrendingUpIcon, StarIcon } from 'lucide-react';
interface Play {
  id: number;
  name: string;
  description: string;
  expectedMetrics: string;
  personas: string[];
  motion: string;
  stage: string;
  vertical: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  timeToImplement: string;
  isFeatured: boolean;
  performanceBadge: string | null;
}
interface PlayCardProps {
  play: Play;
  onClick?: () => void;
}
export function PlayCard({
  play,
  onClick
}: PlayCardProps) {
  const navigate = useNavigate();
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy':
        return 'bg-green-100 text-green-700';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'Hard':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const handleCardClick = () => {
    if (onClick) {
      onClick();
    } else {
      // Navigate to plays library to view this play
      navigate('/plays/library');
    }
  };
  return <div onClick={handleCardClick} className="border border-gray-200 rounded-lg p-5 hover:shadow-lg transition-shadow bg-white cursor-pointer" data-id="element-2428">
      {/* Header */}
      <div className="flex items-start justify-between mb-3" data-id="element-2429">
        <div className="flex-1" data-id="element-2430">
          <div className="flex items-center gap-2 mb-1" data-id="element-2431">
            <h4 className="text-lg font-semibold text-gray-900" data-id="element-2432">{play.name}</h4>
            {play.performanceBadge && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full" data-id="element-2433">
                <StarIcon className="w-3 h-3" data-id="element-2434" />
                {play.performanceBadge}
              </span>}
          </div>
          <p className="text-sm text-gray-600 line-clamp-2" data-id="element-2435">
            {play.description}
          </p>
        </div>
      </div>
      {/* Expected Metrics */}
      <div className="flex items-center gap-2 mb-4 p-3 bg-green-50 rounded-lg" data-id="element-2436">
        <TrendingUpIcon className="w-4 h-4 text-green-600 flex-shrink-0" data-id="element-2437" />
        <span className="text-sm font-medium text-green-700" data-id="element-2438">
          {play.expectedMetrics}
        </span>
      </div>
      {/* Tags */}
      <div className="flex flex-wrap gap-2 mb-4" data-id="element-2439">
        {play.personas.map((persona, index) => <span key={index} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded" data-id="element-2440">
            {persona}
          </span>)}
        <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded" data-id="element-2441">
          {play.motion}
        </span>
        <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded" data-id="element-2442">
          {play.stage}
        </span>
        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded" data-id="element-2443">
          {play.vertical}
        </span>
      </div>
      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200" data-id="element-2444">
        <div className="flex items-center gap-4 text-xs text-gray-500" data-id="element-2445">
          <div className="flex items-center gap-1" data-id="element-2446">
            <ClockIcon className="w-3 h-3" data-id="element-2447" />
            <span data-id="element-2448">{play.timeToImplement}</span>
          </div>
          <span className={`px-2 py-1 rounded text-xs font-medium ${getDifficultyColor(play.difficulty)}`} data-id="element-2449">
            {play.difficulty}
          </span>
        </div>
        <button onClick={e => {
        e.stopPropagation();
      }} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors" data-id="element-2450">
          <DownloadIcon className="w-4 h-4" data-id="element-2451" />
          Import
        </button>
      </div>
    </div>;
}